/**
 * Minimal, dependency-free, XSS-safe Markdown renderer for Help Center guides.
 *
 * Renders to React elements (never dangerouslySetInnerHTML), so admin-authored
 * content can't inject markup. Supports the subset the guides use: headings,
 * bold/italic/inline-code, links, images, ordered/unordered lists, code blocks,
 * tables, horizontal rules, blockquotes, and GitHub-style callouts
 * (> [!NOTE] / [!TIP] / [!WARNING] / [!IMPORTANT] / [!CAUTION]).
 *
 * Styled through theme tokens so it reads correctly in light and dark.
 */

const CALLOUTS = {
  NOTE: { icon: 'fa-circle-info', color: '#3b82f6', label: 'Note' },
  TIP: { icon: 'fa-lightbulb', color: '#10b981', label: 'Tip' },
  IMPORTANT: { icon: 'fa-star', color: '#8b5cf6', label: 'Important' },
  WARNING: { icon: 'fa-triangle-exclamation', color: '#f59e0b', label: 'Warning' },
  CAUTION: { icon: 'fa-hand', color: '#ef4444', label: 'Caution' }
};

function safeHref(href) {
  const h = String(href || '').trim();
  if (/^(https?:|mailto:)/i.test(h)) return h;
  if (h.startsWith('/') || h.startsWith('#')) return h;
  return null;
}

const INLINE_RX =
  /(`[^`]+`)|(!\[[^\]]*\]\([^)]+\))|(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(\*[^*\s][^*]*\*)|(_[^_\s][^_]*_)/g;

/** Parse a single line of inline markdown into React nodes. */
function inline(text, keyBase) {
  const nodes = [];
  let last = 0;
  let m;
  let i = 0;
  INLINE_RX.lastIndex = 0;
  while ((m = INLINE_RX.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const token = m[0];
    const key = `${keyBase}-${i++}`;
    if (m[1]) {
      nodes.push(
        <code key={key} className="help-md-code">
          {token.slice(1, -1)}
        </code>
      );
    } else if (m[2]) {
      const im = /!\[([^\]]*)\]\(([^)]+)\)/.exec(token);
      const src = im ? safeHref(im[2]) : null;
      nodes.push(
        src ? (
          <img key={key} src={src} alt={im[1] || ''} style={{ maxWidth: '100%', borderRadius: 10, margin: '8px 0' }} />
        ) : (
          <span key={key}>{im ? im[1] : token}</span>
        )
      );
    } else if (m[3]) {
      const lm = /\[([^\]]+)\]\(([^)]+)\)/.exec(token);
      const href = lm ? safeHref(lm[2]) : null;
      nodes.push(
        href ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="help-md-link">
            {lm[1]}
          </a>
        ) : (
          <span key={key}>{lm ? lm[1] : token}</span>
        )
      );
    } else if (m[4]) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (m[5]) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    } else if (m[6]) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    last = m.index + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function Table({ rows, keyBase }) {
  const header = rows[0];
  const body = rows.slice(2); // rows[1] is the |---| separator
  return (
    <div style={{ overflowX: 'auto', margin: '12px 0' }}>
      <table className="help-md-table">
        <thead>
          <tr>
            {header.map((c, i) => (
              <th key={i}>{inline(c, `${keyBase}-h-${i}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci}>{inline(c, `${keyBase}-r${ri}-${ci}`)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

/** Parse the full markdown string into an array of block React elements. */
function parseBlocks(md) {
  const lines = String(md ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let k = 0;

  while (i < lines.length) {
    let line = lines[i];

    // Blank line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Code fence
    if (/^```/.test(line.trim())) {
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        buf.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push(
        <pre key={`b${k++}`} className="help-md-pre">
          <code>{buf.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      blocks.push(<hr key={`b${k++}`} className="help-md-hr" />);
      i++;
      continue;
    }

    // Heading
    const hm = /^(#{1,6})\s+(.*)$/.exec(line);
    if (hm) {
      const level = hm[1].length;
      const Tag = `h${Math.min(level + 1, 6)}`; // h1 in content renders as h2-sized etc.
      blocks.push(
        <Tag key={`b${k++}`} className={`help-md-h help-md-h${level}`}>
          {inline(hm[2], `b${k}`)}
        </Tag>
      );
      i++;
      continue;
    }

    // Table (header row followed by a |---| separator)
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(splitRow(lines[i]));
        i++;
      }
      if (rows.length >= 2) {
        blocks.push(<Table key={`b${k++}`} rows={rows} keyBase={`b${k}`} />);
        continue;
      }
    }

    // Blockquote / callout
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      const first = buf[0] ?? '';
      const cm = /^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*(.*)$/i.exec(first.trim());
      if (cm) {
        const meta = CALLOUTS[cm[1].toUpperCase()];
        const bodyLines = [cm[2], ...buf.slice(1)].filter((l) => l !== undefined);
        const bodyText = bodyLines.join('\n').trim();
        blocks.push(
          <div
            key={`b${k++}`}
            className="help-md-callout"
            style={{ borderColor: meta.color, background: `${meta.color}14` }}
          >
            <div className="help-md-callout-head" style={{ color: meta.color }}>
              <i className={`fas ${meta.icon}`} aria-hidden="true" />
              <span>{meta.label}</span>
            </div>
            {bodyText && <div className="help-md-callout-body">{parseBlocks(bodyText)}</div>}
          </div>
        );
      } else {
        blocks.push(
          <blockquote key={`b${k++}`} className="help-md-quote">
            {parseBlocks(buf.join('\n'))}
          </blockquote>
        );
      }
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={`b${k++}`} className="help-md-ol">
          {items.map((it, idx) => (
            <li key={idx}>{inline(it, `b${k}-${idx}`)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={`b${k++}`} className="help-md-ul">
          {items.map((it, idx) => (
            <li key={idx}>{inline(it, `b${k}-${idx}`)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Paragraph (gather contiguous non-blank, non-special lines)
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^```/.test(lines[i].trim()) &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^(-{3,}|\*{3,}|_{3,})$/.test(lines[i].trim())
    ) {
      para.push(lines[i]);
      i++;
    }
    if (para.length) {
      blocks.push(
        <p key={`b${k++}`} className="help-md-p">
          {inline(para.join(' '), `b${k}`)}
        </p>
      );
    }
  }

  return blocks;
}

export default function Markdown({ children }) {
  return <div className="help-md">{parseBlocks(children)}</div>;
}
